/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { InputAdornment } from "@mui/material";
import { useFormContext } from "react-hook-form-mui";
import { textFieldDefaultFieldBased } from "./reactHookFormValidationRules";
import { getPdfGeneratorRecipients } from "../components/Case/CasePdfGenerator/casePdfGeneratorService";
import { ProductUnion } from "../types/ProductUnion";
import { FormElementReactHookFormProps } from "../components/Case/CaseForm/FormElementReactHookForm";

type PdfGeneratorFormElementConfig = Omit<FormElementReactHookFormProps, "product"> & {
  cols?: Partial<{
    xs: number;
    md: number;
  }>;
};

export const getPdfGeneratorFormElementConfig = ({
  formContext,
  isMinimalView,
  product,
  referenceOptions,
  handleRecipientTypeChange,
}: {
  formContext: ReturnType<typeof useFormContext>;
  isMinimalView: boolean;
  product: ProductUnion;
  referenceOptions: Array<{ type: string; label: string }>;
  handleRecipientTypeChange: (value: string) => void;
}): PdfGeneratorFormElementConfig[] => {
  const commonConfig: PdfGeneratorFormElementConfig[] = [];

  const documentTemplate: "customPdf" | "extrajudicialLetter" = formContext.watch("documentTemplate", "customPdf");

  function getReferenceInputs(): PdfGeneratorFormElementConfig[] {
    return [
      {
        cols: {
          xs: 12,
          md: 4,
        },
        type: "SelectElement",
        label: "Aktenzeichenauswahl",
        path: "externalReferenceSelect",
        options: () =>
          [
            {
              value: "free",
              children: "Aktenzeichen frei eingeben",
            },
          ].concat(
            referenceOptions.map((option) => ({
              value: option.type,
              children: option.label,
            })),
          ),
      },
      {
        cols: {
          xs: 12,
          md: 4,
        },
        type: "TextField",
        label: "Aktenzeichen",
        path: "externalReference",
        additionalProps: {
          variant: "outlined",
          slotProps: {
            input: {
              readOnly: formContext.watch("externalReferenceSelect") !== "free",
            },
          },
        },
      },
    ];
  }

  switch (documentTemplate) {
    case "customPdf":
      let customPdfConfig = structuredClone(commonConfig);

      const isCustomPdf = formContext.watch("documentTemplate", "customPdf") === "customPdf";
      const isRecipientTypeCustom = formContext.watch("recipientType") === "custom";

      if (isCustomPdf) {
        customPdfConfig = customPdfConfig.concat([
          {
            cols: {
              xs: 12,
              md: 6,
            },
            type: "SelectElement",
            label: "Empfänger",
            path: "recipientType",
            options: () =>
              getPdfGeneratorRecipients(product).map((recipient) => {
                return {
                  value: recipient.value,
                  children: recipient.label,
                };
              }),
            additionalProps: {
              helperText: "An wen soll das Schreiben adressiert sein?",
              onChange: handleRecipientTypeChange,
            },
          },
          {
            cols: {
              xs: 12,
              md: 6,
            },
            type: "TextField",
            label: "Dateiname",
            path: "filename",
            validators: {
              pattern: {
                value: /^([a-zA-Z]|_|-|[0-9])*$/,
                message: "Bitte nur Buchstaben (keine Umlaute), Ganzzahlen, Binde- und Unterstriche verwenden.",
              },
            },
            additionalProps: {
              slotProps: {
                endAdornment: <InputAdornment position="end">.pdf</InputAdornment>,
              },
              helperText: "Wie soll die Datei benannt werden?",
            },
          },
          {
            cols: {
              xs: 12,
            },
            type: "TextField",
            label: "Beschreibung",
            path: "description",
            validators: textFieldDefaultFieldBased,
          },
        ]);
      }

      if (isCustomPdf && isRecipientTypeCustom) {
        customPdfConfig = customPdfConfig.concat([
          {
            cols: {
              xs: 12,
              md: 6,
            },
            type: "TextField",
            label: "Empfänger",
            path: "address.name",
          },
          {
            cols: {
              xs: 12,
              md: 6,
            },
            type: "TextField",
            label: "Empfänger Zeile 2",
            path: "address.department",
          },
          {
            cols: {
              xs: 12,
              md: 6,
            },
            type: "TextField",
            label: "Empfänger Zeile 3",
            path: "address.subDepartment",
          },
          {
            cols: {
              xs: 12,
              md: 6,
            },
            type: "TextField",
            label: "Straße und Hausnummer",
            path: "address.streetAddress",
          },
          {
            cols: {
              xs: 12,
              md: 6,
            },
            type: "TextField",
            label: "Postleitzahl",
            path: "address.postalCode",
          },
          {
            cols: {
              xs: 12,
              md: 6,
            },
            type: "TextField",
            label: "Ort",
            path: "address.addressLocality",
          },
        ]);
      }

      if (isCustomPdf && !isMinimalView) {
        customPdfConfig = customPdfConfig.concat([
          {
            cols: {
              xs: 12,
              md: 4,
            },
            type: "SelectElement",
            label: "Zustellung per",
            path: "senderMedium",
            options: () => [
              {
                value: "none",
                children: "Keine Anzeige",
              },
              {
                value: "fax",
                children: "per Fax",
              },
              {
                value: "bea",
                children: "über beA",
              },
              {
                value: "email",
                children: "per E-Mail",
              },
              {
                value: "registeredMail",
                children: "per Einschreiben",
              },
            ],
          },
        ]);
      }

      if (
        isCustomPdf &&
        !isRecipientTypeCustom &&
        !isMinimalView &&
        ["insurance", "opposingCarLiabilityInsurance", "opposingLiabilityInsurance"].includes(
          formContext.watch("recipientType"),
        )
      ) {
        customPdfConfig = customPdfConfig.concat([
          {
            cols: {
              xs: 12,
              md: 4,
            },
            type: "TextField",
            label: "Schadennummer",
            path: "caseReferenceNumber",
            additionalProps: {
              slotProps: {
                input: {
                  readOnly: true,
                },
              },
            },
          },
          {
            cols: {
              xs: 12,
              md: 4,
            },
            type: "TextField",
            label: "Versicherungsnummer",
            path: "insurancePolicyNumber",
            additionalProps: {
              slotProps: {
                input: {
                  readOnly: true,
                },
              },
            },
          },
        ]);
      }

      customPdfConfig = customPdfConfig.concat(getReferenceInputs());

      return customPdfConfig;
    case "extrajudicialLetter":
      let extrajudicialLetterPdfConfig = structuredClone(commonConfig);

      extrajudicialLetterPdfConfig = extrajudicialLetterPdfConfig.concat(getReferenceInputs());

      return extrajudicialLetterPdfConfig;
  }
};
